import React from 'react';
import { Check, FileText, Plus, User, X } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu.tsx';
import { TestClientBadge, DnqBadge, ParaTrialBadge } from '../ClientNameWithBadge';
import { isParaTrialClient } from '../../utils/allocationVisibility';
import { useClientStoreVersion } from '../../services/clientStateService';
import { MAX_WORKSPACE_TABS } from '../../hooks/useWorkspaceTabs';
import { describeWorkspaceTab } from './WorkspaceTabStrip';

/**
 * Header tabs (phones): the active tab's client/page plus a tab count; tapping
 * it lists every tab with switch, close and "New tab".
 */
export default function WorkspaceTabSwitcher({
  tabs,
  activeId,
  getScope,
  pageLabelFor,
  canOpenTab,
  onSwitch,
  onClose,
  onNew,
  justOpenedId = null,
}) {
  useClientStoreVersion();
  const activeTab = tabs.find((t) => t.id === activeId) || tabs[0];
  const active = describeWorkspaceTab(activeTab, getScope(activeTab.id), pageLabelFor);
  const ActiveIcon = active.client ? User : FileText;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="inline-flex h-9 min-w-0 max-w-[11rem] items-center gap-1.5 rounded-md border border-[color:color-mix(in_oklch,var(--secondary-foreground)_35%,transparent)] bg-secondary px-2 text-xs font-medium text-secondary-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          aria-label={`Tabs: ${active.title}. ${tabs.length} open`}
        >
          <ActiveIcon className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">{active.clientName || active.pageLabel}</span>
          {active.client?.isTestClient && <TestClientBadge />}
          {active.client?.isDnq === true && <DnqBadge />}
          {isParaTrialClient(active.client) && <ParaTrialBadge />}
          <span className={`ml-0.5 inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded border border-current px-1 text-[10px] leading-none${justOpenedId ? ' bg-primary/15' : ''}`}>
            {tabs.length}
          </span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuLabel>Open tabs</DropdownMenuLabel>
        {tabs.map((tab) => {
          const info = describeWorkspaceTab(tab, getScope(tab.id), pageLabelFor);
          const Icon = info.client ? User : FileText;
          return (
            <DropdownMenuItem
              key={tab.id}
              onSelect={() => onSwitch(tab.id)}
              className="flex items-center gap-2 py-2"
            >
              <Icon className="h-4 w-4 shrink-0" />
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="truncate">{info.clientName || info.pageLabel}</span>
                {info.clientName && <span className="truncate text-xs text-muted-foreground">{info.pageLabel}</span>}
              </span>
              {tab.id === activeId && <Check className="h-4 w-4 shrink-0" aria-label="Current tab" />}
              <button
                type="button"
                className="shrink-0 rounded-full p-1.5 hover:bg-muted-foreground/20 focus:outline-none focus:ring-2 focus:ring-ring"
                aria-label={`Close tab ${info.title}`}
                onPointerUp={(e) => e.stopPropagation()}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClose(tab.id); }}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </DropdownMenuItem>
          );
        })}
        <DropdownMenuSeparator />
        <DropdownMenuItem disabled={!canOpenTab} onSelect={onNew} className="gap-2">
          <Plus className="h-4 w-4" />
          {canOpenTab ? 'New tab' : `Max ${MAX_WORKSPACE_TABS} tabs open`}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
